import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { EMPLOYEE_SHIFT } from "../../constants/api";
import { ChevronLeft, Save, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { Input, Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { configBranchLabel, withConfigBranch } from "../../utils/branchSession";

dayjs.extend(duration);

const EmployeeShiftAddEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fromEdit, editDetails, isActive } = location.state || {};

    const { create, update } = useApiMethods();
    const today = dayjs().format("YYYY-MM-DD");

    // STATE
    const [formData, setFormData] = useState({
        id: null,
        code: "",
        shift_name: "",
        shift_timings_from: null,
        shift_timings_to: null,
        total_shift_hrs: "",
        total_break_hrs: null,
        break_1st_start_time: null,
        break_1st_end_time: null,
        lunch_start_time: null,
        lunch_end_time: null,
        break_2nd_start_time: null,
        break_2nd_end_time: null,
        notes: "",
        is_active: true,
        is_draft: false,
    });

    const [loading, setLoading] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [timeDifference, setTimeDifference] = useState(null);

    // FIXED: Parse time function matching old code
    const parseTime = useCallback((time, isShiftTime = false) => {
        if (!time) return null;
        
        try {
            // If it's already a dayjs object, return it
            if (dayjs.isDayjs(time)) return time;
            
            // For shift timings (from/to) which come in "h:mm A" format
            if (isShiftTime && typeof time === 'string') {
                if (time.includes('AM') || time.includes('PM')) {
                    return dayjs(time, 'h:mm A');
                } else {
                    // If it's in HH:mm:ss format, convert to dayjs
                    return dayjs(`${today}T${time}`);
                }
            }
            
            // For other times in HH:mm:ss format
            if (typeof time === 'string' && time.includes(':')) {
                return dayjs(`${today}T${time}`);
            }
            
            return null;
        } catch (error) {
            console.error('Error parsing time:', time, error);
            return null;
        }
    }, [today]);

    // FIXED: Load edit details matching old code
    useEffect(() => {
        if (fromEdit && editDetails) {
            const updatedFormData = {
                id: editDetails.id,
                code: editDetails.code || "",
                shift_name: editDetails.shift_name || "",
                notes: editDetails.notes || "",
                is_active: editDetails.is_active,
                shift_timings_from: parseTime(editDetails.shift_timings_from, true),
                shift_timings_to: parseTime(editDetails.shift_timings_to, true),
                total_shift_hrs: editDetails.total_shift_hrs || "",
                total_break_hrs: parseTime(editDetails.total_break_hrs),
                break_1st_start_time: parseTime(editDetails.break_1st_start_time),
                break_1st_end_time: parseTime(editDetails.break_1st_end_time),
                lunch_start_time: parseTime(editDetails.lunch_start_time),
                lunch_end_time: parseTime(editDetails.lunch_end_time),
                break_2nd_start_time: parseTime(editDetails.break_2nd_start_time),
                break_2nd_end_time: parseTime(editDetails.break_2nd_end_time),
            };

            setFormData(updatedFormData);
            setStartTime(parseTime(editDetails.shift_timings_from, true));
            setEndTime(parseTime(editDetails.shift_timings_to, true));
            setTimeDifference(editDetails.total_shift_hrs || null);
        }
    }, [fromEdit, editDetails, parseTime]);

    // FIXED: Auto-calculate total shift hours matching old code logic
    useEffect(() => {
        const calculateTimeDifference = (start, end) => {
            if (!start || !end || !start.isValid() || !end.isValid()) return;

            let diff;
            let adjustedEnd = end;

            // Handle overnight shifts (end time earlier than start time)
            if (end.isBefore(start)) {
                adjustedEnd = end.add(1, 'day');
            }

            const startMs = Math.floor(start.valueOf() / 1000) * 1000;
            const endMs = Math.floor(adjustedEnd.valueOf() / 1000) * 1000;
            diff = endMs - startMs;

            const msec = diff;
            const hh = Math.floor(msec / 1000 / 60 / 60);
            const mm = Math.floor((msec - (hh * 1000 * 60 * 60)) / 1000 / 60);

            if (hh < 0 || isNaN(hh)) {
                setTimeDifference(null);
                setFormData(prev => ({ ...prev, total_shift_hrs: "" }));
                return;
            }

            const totalShiftHrs = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
            setFormData(prev => ({ ...prev, total_shift_hrs: totalShiftHrs }));
            setTimeDifference(totalShiftHrs);
        };

        if (formData.shift_timings_from && formData.shift_timings_to) {
            calculateTimeDifference(formData.shift_timings_from, formData.shift_timings_to);
        }
    }, [formData.shift_timings_from, formData.shift_timings_to]);

    // HANDLERS
    const handleChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleTimeChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        
        // Update startTime and endTime for shift timings
        if (key === "shift_timings_from") {
            setStartTime(value);
            if (value && formData.shift_timings_to) {
                // Recalculate time difference
                const startMs = Math.floor(value.valueOf() / 1000) * 1000;
                const endMs = Math.floor(formData.shift_timings_to.valueOf() / 1000) * 1000;
                const diff = endMs < startMs ? endMs + (24 * 60 * 60 * 1000) - startMs : endMs - startMs;
                
                const hh = Math.floor(diff / 1000 / 60 / 60);
                const mm = Math.floor((diff - (hh * 1000 * 60 * 60)) / 1000 / 60);
                
                if (hh >= 0 && !isNaN(hh)) {
                    const totalShiftHrs = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
                    setFormData(prev => ({ ...prev, total_shift_hrs: totalShiftHrs }));
                }
            }
        } else if (key === "shift_timings_to") {
            setEndTime(value);
            if (value && formData.shift_timings_from) {
                // Recalculate time difference
                const startMs = Math.floor(formData.shift_timings_from.valueOf() / 1000) * 1000;
                const endMs = Math.floor(value.valueOf() / 1000) * 1000;
                const diff = endMs < startMs ? endMs + (24 * 60 * 60 * 1000) - startMs : endMs - startMs;
                
                const hh = Math.floor(diff / 1000 / 60 / 60);
                const mm = Math.floor((diff - (hh * 1000 * 60 * 60)) / 1000 / 60);
                
                if (hh >= 0 && !isNaN(hh)) {
                    const totalShiftHrs = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
                    setFormData(prev => ({ ...prev, total_shift_hrs: totalShiftHrs }));
                }
            }
        }
    };

    const handleCancel = () => {
        navigate("/configuration/shiftconfiguration");
    };

    // FIXED: Prepare payload matching old code format
    const preparePayload = (isDraft = false) => {
        const formatTime = (time) => {
            if (!time || !time.isValid()) return null;
            return time.format("HH:mm:ss");
        };

        return withConfigBranch({
            ...formData,
            shift_timings_from: formatTime(formData.shift_timings_from),
            shift_timings_to: formatTime(formData.shift_timings_to),
            total_break_hrs: formatTime(formData.total_break_hrs),
            break_1st_start_time: formatTime(formData.break_1st_start_time),
            break_1st_end_time: formatTime(formData.break_1st_end_time),
            lunch_start_time: formatTime(formData.lunch_start_time),
            lunch_end_time: formatTime(formData.lunch_end_time),
            break_2nd_start_time: formatTime(formData.break_2nd_start_time),
            break_2nd_end_time: formatTime(formData.break_2nd_end_time),
            is_draft: isDraft,
        });
    };

    // FIXED: Handle save with draft support
    const handleSave = useCallback(async (isDraft = false) => {
        setLoading(true);
        try {
            const payload = preparePayload(isDraft);
            const res = fromEdit
                ? await update(EMPLOYEE_SHIFT, payload)
                : await create(EMPLOYEE_SHIFT, payload);

            if (res?.status === 200 || res?.status === 201 || res?.status === "success") {
                const message = isDraft ? "Shift saved as draft successfully" : 
                              fromEdit ? "Shift updated successfully" : "Shift created successfully";
                ToastMsg("success", message);
                navigate("/configuration/shiftconfiguration");
            } else if (res?.status === "fail") {
                ToastMsg("error", res.message);
            }
        } catch (err) {
            console.error("Error saving shift:", err);
            ToastMsg("error", err.message || "Failed to save shift");
        } finally {
            setLoading(false);
        }
    }, [formData, fromEdit, update, create, navigate]);

    // FIXED: Validation and save handlers matching old code
    const handleSaveClick = useCallback(() => {
        // Basic validation
        if (!formData.shift_name?.trim()) {
            ToastMsg("error", "Shift Name is required");
            return;
        }
        if (!formData.shift_timings_from) {
            ToastMsg("error", "Shift Start Time is required");
            return;
        }
        if (!formData.shift_timings_to) {
            ToastMsg("error", "Shift End Time is required");
            return;
        }
        if (!formData.total_break_hrs) {
            ToastMsg("error", "Total Break Hours is required");
            return;
        }

        handleSave(false);
    }, [formData, handleSave]);

    const handleSaveAsDraft = useCallback(() => {
        handleSave(true);
    }, [handleSave]);

    // Check if form has data for draft button (matching old code logic)
    const hasFormData = Object.entries(formData).some(([key, value]) => 
        key !== 'is_active' && key !== 'id' && 
        value !== "" && value !== null && value !== undefined && value !== false
    );

    // FIXED: Safe TimePicker component
    const SafeTimePicker = ({ value, onChange, name, use12Hours = true, format = "h:mm A", ...props }) => (
        <TimePicker
            value={value && value.isValid() ? value : null}
            onChange={(time) => onChange(name, time)}
            format={format}
            use12Hours={use12Hours}
            size="large"
            {...props}
        />
    );

    // RENDER
    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeft className="h-5 w-5 cursor-pointer" onClick={handleCancel} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Shift Policy" : "Add Shift Policy"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Configure working hours and breaks for {configBranchLabel()}. Updating this shift does not change another branch.
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <span
                        className={cn(
                            "text-sm font-medium",
                            formData.is_active ? "text-green-600" : "text-gray-500"
                        )}
                    >
                        {formData.is_active ? "Active" : "Inactive"}
                    </span>
                    <button
                        type="button"
                        onClick={() => handleChange("is_active", !formData.is_active)}
                        disabled={fromEdit && !isActive}
                        className={cn(
                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                            formData.is_active ? "bg-sidebar-primary" : "bg-gray-300",
                            fromEdit && !isActive && "opacity-50 cursor-not-allowed"
                        )}
                    >
                        <span
                            className={cn(
                                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                                formData.is_active ? "translate-x-4" : "translate-x-1"
                            )}
                        />
                    </button>
                </div>
            </div>

            {/* Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Shift Code */}
                    {fromEdit && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Shift Code <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formData.code}
                                onChange={(e) => handleChange("code", e.target.value)}
                                placeholder="Enter shift code"
                                disabled={fromEdit}
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                )}
                            />
                        </div>
                    )}

                    {/* Shift Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Shift Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.shift_name}
                            onChange={(e) => handleChange("shift_name", e.target.value)}
                            placeholder="Enter shift name"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
                            )}
                        />
                    </div>

                    {/* Shift From */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Shift Start Time <span className="text-red-500">*</span>
                        </label>
                        <SafeTimePicker
                            value={formData.shift_timings_from}
                            onChange={handleTimeChange}
                            name="shift_timings_from"
                            className="w-full"
                        />
                    </div>

                    {/* Shift To */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Shift End Time <span className="text-red-500">*</span>
                        </label>
                        <SafeTimePicker
                            value={formData.shift_timings_to}
                            onChange={handleTimeChange}
                            name="shift_timings_to"
                            className="w-full"
                        />
                    </div>

                    {/* Total Shift Hours */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Total Shift Hours
                        </label>
                        <Input
                            value={formData.total_shift_hrs || ""}
                            disabled
                            placeholder="Auto calculated by shift timings"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                            )}
                        />
                    </div>

                    {/* Total Break Hours */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Total Break Hours <span className="text-red-500">*</span>
                        </label>
                        <SafeTimePicker
                            value={formData.total_break_hrs}
                            onChange={handleTimeChange}
                            name="total_break_hrs"
                            use12Hours={false}
                            format="HH:mm"
                            className="w-full"
                        />
                    </div>

                    {/* 1st Break */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            1st Break (From - To)
                        </label>
                        <div className="flex gap-2">
                            <SafeTimePicker
                                value={formData.break_1st_start_time}
                                onChange={handleTimeChange}
                                name="break_1st_start_time"
                                className="w-full"
                                placeholder="Start"
                            />
                            <SafeTimePicker
                                value={formData.break_1st_end_time}
                                onChange={handleTimeChange}
                                name="break_1st_end_time"
                                className="w-full"
                                placeholder="End"
                            />
                        </div>
                    </div>

                    {/* Lunch Time */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Lunch Time (From - To)
                        </label>
                        <div className="flex gap-2">
                            <SafeTimePicker
                                value={formData.lunch_start_time}
                                onChange={handleTimeChange}
                                name="lunch_start_time"
                                className="w-full"
                                placeholder="Start"
                            />
                            <SafeTimePicker
                                value={formData.lunch_end_time}
                                onChange={handleTimeChange}
                                name="lunch_end_time"
                                className="w-full"
                                placeholder="End"
                            />
                        </div>
                    </div>

                    {/* 2nd Break */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            2nd Break (From - To)
                        </label>
                        <div className="flex gap-2">
                            <SafeTimePicker
                                value={formData.break_2nd_start_time}
                                onChange={handleTimeChange}
                                name="break_2nd_start_time"
                                className="w-full"
                                placeholder="Start"
                            />
                            <SafeTimePicker
                                value={formData.break_2nd_end_time}
                                onChange={handleTimeChange}
                                name="break_2nd_end_time"
                                className="w-full"
                                placeholder="End"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="md:col-span-2 space-y-2">
                        <label className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleChange("notes", e.target.value)}
                            placeholder="Enter any notes about this shift"
                            {...modalDescriptionFieldProps}
                            className={modalTextareaClass}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    {/* Draft Button - Only show in create mode */}
                    {!fromEdit && (
                        <Button
                            onClick={handleSaveAsDraft}
                            disabled={loading || !hasFormData}
                            className={cn(
                                "bg-gray-600 hover:bg-gray-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (!hasFormData) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Save as Draft
                        </Button>
                    )}

                    <Button
                        variant="brand"
                        onClick={handleSaveClick}
                        disabled={loading}
                        className="flex items-center gap-2 transition-colors duration-200"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update" : "Save Policy")}
                    </Button>
                </div>

                {/* Note for edit mode */}
                {fromEdit && (
                    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                            Note: Changed shift will reflect tomorrow
                        </p>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default EmployeeShiftAddEdit;